import { format as formatDateFns, isValid } from "date-fns";
import { FM_DD_MM_YYYY, FM_DD_MM_YYYY_HH_MM } from "const/format.const";
import i18next from "i18next";

// ==================== Types ====================

export interface Device {
  id: string;
  accountId: string;
  accountName: string;
  deviceName: string;
  phone: string;
  email?: string;
  role: string;
  type: string;
  os: string;
  status: string;
  firstLoginDate: number | string | null;
  version: string;
  lastActivity: number | string | null;
  osVersion?: string;
  address?: string;
  unit?: string;
  department?: string;
}

export interface FilterCriteria {
  searchText: string;
  selectedTypes: string[];
  selectedOS: string[];
  selectedVersions: string[];
}

export interface DeviceGroup {
  accountId: string;
  accountName: string;
  phone?: string;
  avatarUrl?: string;
  devices: Device[];
}

// ==================== Date Formatting ====================

/**
 * Format a timestamp to DD/MM/YYYY using date-fns.
 */
export function formatDate(timestamp: number | string | Date | null | undefined): string {
  if (timestamp === null || timestamp === undefined) return "";
  const date = new Date(timestamp);
  if (!isValid(date)) return "";
  return formatDateFns(date, FM_DD_MM_YYYY);
}

/**
 * Format a timestamp to DD/MM/YYYY HH:mm using date-fns.
 */
export function formatDateTime(timestamp: number | string | Date | null | undefined): string {
  if (timestamp === null || timestamp === undefined) return "\u2014";
  const date = new Date(timestamp);
  if (!isValid(date)) return "\u2014";
  return formatDateFns(date, FM_DD_MM_YYYY_HH_MM);
}

/**
 * Convert a timestamp to a Vietnamese relative time string.
 */
export function formatRelativeTime(timestamp: number | string | Date | null | undefined): string {
  if (timestamp === null || timestamp === undefined) return "\u2014";
  const date = new Date(timestamp);
  if (!isValid(date)) return "\u2014";

  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 0) return "\u2014";

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return i18next.t("deviceManagement:TXT_DEVICE_TIME_JUST_NOW");
  if (diffMinutes < 60) return i18next.t("deviceManagement:FM_DEVICE_TIME_MINUTES_AGO", { count: diffMinutes });
  if (diffHours < 24) return i18next.t("deviceManagement:FM_DEVICE_TIME_HOURS_AGO", { count: diffHours });
  return i18next.t("deviceManagement:FM_DEVICE_TIME_DAYS_AGO", { count: diffDays });
}

// ==================== Filter Helpers ====================

/**
 * Filter devices based on all active criteria.
 */
export function filterDevices(devices: Device[], criteria: FilterCriteria): Device[] {
  const { searchText, selectedTypes, selectedOS, selectedVersions } = criteria;

  return devices.filter((device) => {
    if (searchText) {
      const search = searchText.toLowerCase();
      const phone = (device.phone || "").toLowerCase();
      const email = (device.email || "").toLowerCase();
      if (!phone.includes(search) && !email.includes(search)) {
        return false;
      }
    }

    if (!selectedTypes.includes("all") && !selectedTypes.includes(device.type)) {
      return false;
    }

    if (!selectedOS.includes("all") && !selectedOS.includes(device.os)) {
      return false;
    }

    if (!selectedVersions.includes("all") && !selectedVersions.includes(device.version)) {
      return false;
    }

    return true;
  });
}

/**
 * Group devices by account.
 */
export function groupDevicesByAccount(devices: Device[]): DeviceGroup[] {
  const groupMap: Record<string, DeviceGroup> = {};
  const groupOrder: string[] = [];

  devices.forEach((device) => {
    if (!groupMap[device.accountId]) {
      groupMap[device.accountId] = {
        accountId: device.accountId,
        accountName: device.accountName,
        devices: [],
      };
      groupOrder.push(device.accountId);
    }
    groupMap[device.accountId].devices.push(device);
  });

  return groupOrder.map((accountId) => groupMap[accountId]);
}

// ==================== API Mapping ====================

const DEVICE_TYPE_MAP: Record<number, string> = { 1: "Mobile", 2: "Mobile" };

const OS_DISPLAY: Record<number, string> = {
  1: "Android",
  2: "iOS",
  3: "Windows",
  4: "Linux",
  5: "MacOS",
};

interface ApiDevice {
  id: string;
  accountId?: string;
  accountName?: string;
  phone?: string;
  modelNumber?: string;
  options?: string;
  type?: number;
  state?: number;
  blocked?: boolean;
  masterF?: number;
  created?: number | string;
  version?: number;
  lastSeen?: number | string;
}

interface AccountInfo {
  accountId: string;
  accountName: string;
  phone?: string;
  avatarUrl?: string;
  devices?: ApiDevice[];
}

/**
 * Extract a readable phone string from the API phone field.
 */
function extractPhone(phone: string | undefined | null): string {
  if (!phone) return "\u2014";
  try {
    const parsed = JSON.parse(phone);
    return parsed.phone || parsed.name || "\u2014";
  } catch {
    return phone;
  }
}

/**
 * Map a single API device object to the display row format.
 */
export function mapApiDeviceToRow(apiDevice: ApiDevice, accountInfo: AccountInfo): Device {
  let options: Record<string, string | number> = {};
  try {
    options = JSON.parse(apiDevice.options || "{}");
  } catch {
    // ignore parse error
  }

  const now = Date.now();
  const lastSeen = apiDevice.lastSeen ? Number(apiDevice.lastSeen) : 0;
  const isOnline = lastSeen > 0 && now - lastSeen < 60000;

  let status: string;
  if (apiDevice.blocked) {
    status = i18next.t("deviceManagement:TXT_DEVICE_STATUS_BLOCKED");
  } else if (apiDevice.state === 0) {
    status = i18next.t("deviceManagement:TXT_DEVICE_STATUS_LOGGED_OUT");
  } else if (isOnline) {
    status = i18next.t("deviceManagement:TXT_DEVICE_STATUS_ACTIVE");
  } else {
    status = i18next.t("deviceManagement:TXT_DEVICE_STATUS_ABSENT");
  }

  return {
    id: apiDevice.id,
    accountId: apiDevice.accountId || accountInfo.accountId,
    accountName: accountInfo.accountName || apiDevice.accountName || "",
    deviceName: (options.device_name as string) || apiDevice.modelNumber || "\u2014",
    phone: extractPhone(accountInfo.phone || apiDevice.phone),
    role: apiDevice.masterF === 1 ? i18next.t("deviceManagement:TXT_DEVICE_ROLE_MASTER") : i18next.t("deviceManagement:TXT_DEVICE_ROLE_SECONDARY"),
    type: DEVICE_TYPE_MAP[apiDevice.type ?? 0] || "Desktop",
    os: OS_DISPLAY[apiDevice.type ?? 0] || "Khác",
    status,
    firstLoginDate: apiDevice.created ?? null,
    version: (options.device_version as string) || "\u2014",
    osVersion: (options.os_version as string) || "\u2014",
    lastActivity: apiDevice.lastSeen ?? null,
  };
}

/**
 * Map the entire API grouped response to the format expected by DeviceTable.
 */
export function mapApiGroupsToDisplay(apiGroups: AccountInfo[] | null | undefined): DeviceGroup[] {
  if (!apiGroups || !Array.isArray(apiGroups)) return [];

  return apiGroups.map((group) => ({
    accountId: group.accountId,
    accountName: group.accountName,
    phone: group.phone,
    avatarUrl: group.avatarUrl,
    devices: (group.devices || []).map((device) => mapApiDeviceToRow(device, group)),
  }));
}
